/** Every word of the Companies page (SA-1456 spec §7.2). Role words are the typeahead's (`companyText.roleLabel`). */
import type { CompanyUsage } from '@/types/companies'

export const PAGE_TITLE = 'Companies'
export const PAGE_SENTENCE =
  'The companies this deployment works with: customers, forwarders, brokers, shippers, consignees and our own firms. Open one to correct it, remove a spelling, or merge two rows that are one firm.'

/** The role chips: a filter id and its words. `ours` is a carrier or an affiliate. */
export const FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'customer', label: 'Customers' },
  { id: 'forwarder', label: 'Forwarders' },
  { id: 'broker', label: 'Brokers' },
  { id: 'shipper', label: 'Shippers' },
  { id: 'consignee', label: 'Consignees' },
  { id: 'ours', label: 'Our firms' },
] as const
export type FilterId = (typeof FILTERS)[number]['id']
export const FILTER_GROUP = 'Show companies by role'
export const SHOW_MERGED = 'Show merged companies'
export const MERGED_NOTE =
  "A merged company's counts are its own; the company it was merged into already counts them."

export const LIST_LABEL = 'Companies'
export const COLUMNS = {
  name: 'Name', short: 'Short name', legal: 'Legal name', country: 'Country', roles: 'Roles',
  spellings: 'Spellings', usage: 'What names it',
} as const
export const EMPTY = 'No companies yet. A company is added when someone links a party field to a new name.'
export const EMPTY_FILTERED = 'No company has this role.'
export const LIST_UNAVAILABLE = "Couldn't load the companies."
export const NO_SPELLINGS = 'None'
export const NOTHING_NAMES = 'Nothing yet'
export const NONE = '—'

export const editLabel = (name: string): string => `Edit ${name}`
export const mergedInto = (name: string): string => `merged into ${name}`
export const spellingsCount = (n: number): string => (n === 1 ? '1 spelling' : `${n} spellings`)

/** "3 contracts, 1 intake link", in the server's order, each in the form the server gives for its count;
 * empty when nothing names it. */
export function usageText(usage: readonly CompanyUsage[]): string {
  return usage.map((u) => `${u.count} ${u.count === 1 ? u.label_one : u.label}`).join(', ')
}

// --- the drawer ---

export const FIELDS = {
  name: 'Name', short: 'Short name', legal: 'Legal name', country: 'Country', registration: 'Registration',
  roles: 'Roles',
} as const
export const COUNTRY_HINT = 'Two letters, such as BG.'
export const SAVE = 'Save'
export const SAVING = 'Saving…'
export const SAVED = 'Saved.'
export const SAVE_FAILED = "Couldn't save the company."
export const CHANGED = 'Someone changed this company meanwhile.'
export const RELOAD = 'Reload'
export const RELOAD_FAILED = "Couldn't reload the company."
/** Reload found the company merged away meanwhile: it now reads as another. */
export const wasMergedInto = (from: string, into: string): string => `${from} was merged into ${into}.`
export const openCompany = (name: string): string => `Open ${name}`
export const lastChanged = (name: string): string => `Last changed by ${name}`

export const SPELLINGS_HEADING = 'Spellings'
export const SPELLINGS_NONE = 'No other spellings.'
export const FROM_MERGE = 'from a merge'
export const FORMER_NAME = 'a former name'
export const REMOVE = 'Remove'
export const removeLabel = (text: string): string => `Remove ${text}`
export const REMOVE_FAILED = "Couldn't remove the spelling."

export const USAGE_HEADING = 'What names it'

export const MERGE_OPEN = 'Merge into…'
export const MERGE_LABEL = 'Merge into'
export const MERGE_HINT = 'Pick the company this one is the same firm as.'
export const MERGE = 'Merge'
export const MERGING = 'Merging…'
export const CANCEL = 'Cancel'
export const MERGE_SELF = 'Pick another company: a company cannot be merged into itself.'
export const MERGE_FAILED = "Couldn't merge the company."
export const mergeConfirm = (from: string, into: string): string =>
  `Merge ${from} into ${into}? ${from}'s names and spellings become ${into}'s spellings, and its roles join ${into}'s. Nothing that names ${from} is rewritten: it reads as ${into} from now on.`
export const merged = (from: string, into: string): string => `Merged ${from} into ${into}.`
